import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { forwardRef, useId } from 'react'

import { Tooltip } from '@repo/ui'
import { cn } from '@repo/utils'

import type { InputHTMLAttributes, ReactNode } from 'react'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'maxLength' | 'minLength' | 'type'> {
  children?: ReactNode
  className?: string
  contentClassName?: string
  error?: ReactNode
  inputClassName?: string
  label?: ReactNode
  labelComment?: ReactNode
  labelInfo?: ReactNode
  transparent?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      autoComplete = 'off',
      children,
      className,
      contentClassName,
      error,
      inputClassName,
      label,
      labelComment,
      labelInfo,
      name,
      required,
      transparent = false,
      ...props
    },
    ref,
  ) => {
    const randId = useId()
    const htmlId = id || name || randId

    const input = (
      <input
        {...props}
        id={htmlId}
        autoComplete={autoComplete}
        className={cn(
          'inline-block appearance-none cursor-pointer outline-0 rounded-md border-0 shadow-sm px-1.5 py-1 m-0 bg-white text-inherit text-transparent',
          "after:content-['✔']",
          'ring-1 ring-inset ring-greyDarkBlue ring-offset-1',
          !transparent && 'focus:ring-greyDarkestBlue focus:bg-greyLight',
          !transparent && 'hover:ring-greyDarkestBlue hover:bg-greyLight',

          'checked:text-current checked:ring-greyDarkBlue',
          !transparent && 'checked:focus:ring-greyDarkestBlue checked:focus:bg-greyLight',
          !transparent && 'checked:hover:ring-greyDarkestBlue checked:hover:bg-greyLight',

          'disabled:ring-grey disabled:bg-greyLightest disabled:cursor-default',
          !transparent && 'disabled:hover:ring-grey disabled:hover:bg-greyLightest',
          // 'read-only:ring-greyLight read-only:focus:ring-grey',
          transparent &&
            'shadow-none ring-transparent focus:ring-transparent focus:bg-transparent disabled:ring-transparent disabled:bg-transparent checked:ring-transparent checked:bg-transparent',
          error &&
            'ring-red focus:ring-red hover:ring-red disabled:ring-red checked:text-red checked:focus:ring-red checked:hover:ring-red',
          inputClassName,
        )}
        name={name || htmlId}
        ref={ref}
        required={required}
        type="checkbox"
      />
    )

    return (
      <div className={cn('Checkbox', className)}>
        {label || labelComment || labelInfo ? (
          <label className="block font-bold mb-1" htmlFor={htmlId}>
            {label}
            {labelComment ? <small className="font-normal pl-1 leading-4">{labelComment}</small> : null}
            {required ? <sup className="text-red pl-1 leading-4 align-top">*</sup> : ''}
            {labelInfo ? (
              <Tooltip content={labelInfo}>
                <InformationCircleIcon className="size-4 ml-1 leading-4 text-blue align-top cursor-pointer" />
              </Tooltip>
            ) : null}
          </label>
        ) : null}
        <div className={cn('flex items-start gap-1 p-1 border rounded-md', error && 'border-red', contentClassName)}>
          {error ? (
            <Tooltip className="border-redLight stroke-redLight" content={error} initialOpen position="top-start">
              {input}
            </Tooltip>
          ) : (
            input
          )}
          <label className="grow" htmlFor={htmlId}>
            {children}
          </label>
        </div>
      </div>
    )
  },
)

Checkbox.displayName = 'Checkbox'
